import { ProDescriptions } from '@ant-design/pro-components';
import { history, useAccess } from '@umijs/max';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  message,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import React, {
  type CSSProperties,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CrmList } from '@/components/CrmList';
import QuietEmpty from '@/components/QuietEmpty';
import Empty from '@/components/QuietEmpty';
import {
  ActivityPanel,
  ActivityPanelFollowUps,
} from '@/modules/zy-crm/components/activity-panel/export';
import { AiDetailPanel } from '@/modules/zy-crm/components/ai-detail-panel';
import { BusinessModal } from '@/modules/zy-crm/components/business-modal';
import { BusinessTripRelationPanel } from '@/modules/zy-crm/components/business-trip-relation-panel';
import {
  type CalendarEventFormValues,
  calendarEventFormDefaultValues,
  createCalendarRelationFormValue,
  normalizeCalendarRelationPayload,
} from '@/modules/zy-crm/components/calendar-event-form';
import { ContactReferenceLink } from '@/modules/zy-crm/components/contact-detail-drawer';
import { ContactEditModal } from '@/modules/zy-crm/components/contact-edit-modal';
import { ContactOpportunityCreateModal } from '@/modules/zy-crm/components/contact-opportunity-create-modal';
import { ContractCenterSyncAction } from '@/modules/zy-crm/components/contract-center-sync-action';
import {
  ContractEditModal,
  type ContractEditModalMode,
} from '@/modules/zy-crm/components/contract-edit-modal';
import { EntityTagEditor } from '@/modules/zy-crm/components/crm-tags';
import { CrmTaskRelationPanel } from '@/modules/zy-crm/components/crm-task-relation-panel';
import { CustomerReferenceLink } from '@/modules/zy-crm/components/customer-detail-drawer';
import { DetailNavigation } from '@/modules/zy-crm/components/detail-navigation';
import { DetailSurface } from '@/modules/zy-crm/components/detail-surface';
import { FollowUpCreateModal } from '@/modules/zy-crm/components/follow-up-create-modal';
import { LeadReferenceLink } from '@/modules/zy-crm/components/lead-detail-drawer';
import { OpportunityEditModal } from '@/modules/zy-crm/components/opportunity-edit-modal';
import { OpportunityFollowButton } from '@/modules/zy-crm/components/opportunity-follow-button';
import { OpportunityLifecycleModal } from '@/modules/zy-crm/components/opportunity-lifecycle-modal';
import { OpportunityToLeadModal } from '@/modules/zy-crm/components/opportunity-to-lead-modal';
import { QuoteEditorModal } from '@/modules/zy-crm/components/quote-editor-modal';
import { ReferralSection as UnifiedReferralSection } from '@/modules/zy-crm/components/referral-section';
import { TeamMembersPanel } from '@/modules/zy-crm/components/team-members-panel';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import type { RelationCreateContext } from '@/modules/zy-crm/types/relation-create-context';
import { canEditContract } from '@/modules/zy-crm/utils/contract-center';
import {
  type CrmScopedViewMode,
  resolveCrmScopedViewModeFromPathname,
} from '@/modules/zy-crm/utils/crm-view';
import {
  defaultDetailSection,
  detailSectionLabel,
  normalizeDetailSections,
} from '@/modules/zy-crm/utils/detail-navigation';
import {
  formatCrmDate,
  resolveRelationText,
  toApiDateTime,
} from '@/modules/zy-crm/utils/display';
import { opportunityPriorityLabel } from '@/modules/zy-crm/utils/opportunity-priority';
import {
  resolveOpportunityOutcome,
  resolveOpportunityOutcomeColor,
  resolveOpportunityOutcomeFromStage,
  resolveOpportunityOutcomeLabel,
} from '@/modules/zy-crm/utils/opportunity-status';
import {
  fetchContactOptions,
  fetchCustomerOptions,
  fetchPipelineOptions,
  fetchStageOptions,
  mergeOptions,
  type RelationOption,
  resolveRelationLabel,
} from '@/modules/zy-crm/utils/relation-options';
import {
  createCalendarEvent,
  updateCalendarEvent,
} from '@/services/bff/calendar.api';
import {
  queryOpportunityDrawerProfile,
  refreshOpportunityProjections,
  touchOpportunity,
  updateOpportunity,
} from '@/services/bff/opportunity.api';
import {
  createReferral,
  deleteReferral,
  updateReferral,
} from '@/services/bff/referral.api';
import type { CalendarEventItem } from '@/services/bff/types/calendar';
import type {
  OpportunityDrawerContactItem,
  OpportunityDrawerContractItem,
  OpportunityDrawerFollowUpItem,
  OpportunityDrawerProductItem,
  OpportunityDrawerProfile,
  OpportunityDrawerQuoteItem,
  OpportunityDrawerRelatedPage,
  OpportunityDrawerSection,
  OpportunityItem,
  OpportunityViewMode,
  UpdateOpportunityPayload,
} from '@/services/bff/types/opportunity';
import type { ReferralItem } from '@/services/bff/types/referral';
import { notifyOpportunityConvertedToLead } from '@/services/crmRecordEvents';
import { OpportunityAdvanceSummary } from './advance-summary';
import styles from './style.module.less';

const { Paragraph, Text, Title } = Typography;

type OpportunityDrawerSectionKey =
  | 'advance'
  | 'activities'
  | 'followUps'
  | 'details'
  | 'teamMembers'
  | 'referrals'
  | 'contacts'
  | 'tasks'
  | 'businessTrips'
  | 'quotes'
  | 'contracts'
  | 'products';

type OpportunityDrawerOpenInput = {
  viewMode?: OpportunityViewMode;
  opportunityUuid?: string;
  opportunityName?: ReactNode;
};

type OpportunityDetailDrawerContextValue = {
  openOpportunity: (input: OpportunityDrawerOpenInput) => void;
};

type OpportunityDetailDrawerProviderProps = {
  children: ReactNode;
};

type OpportunityReferenceLinkProps = {
  viewMode?: OpportunityViewMode;
  opportunityUuid?: string;
  opportunityName?: ReactNode;
  fallbackName?: ReactNode;
  emptyText?: ReactNode;
  canViewDetail?: boolean;
  style?: CSSProperties;
};

type OpportunityDetailDrawerProps = {
  viewMode?: OpportunityViewMode;
  open: boolean;
  opportunityUuid?: string;
  fallbackName?: ReactNode;
  onClose: () => void;
};

type SectionPanelProps = {
  title: string;
  hint?: string;
  extra?: ReactNode;
  children: ReactNode;
};

type OpportunityDrawerLookups = {
  customerOptions: RelationOption[];
  memberOptions: RelationOption[];
  pipelineOptions: RelationOption[];
  stageOptions: RelationOption[];
};

type OpportunityIncomingReferralFormValues = {
  incomingReferrerType?: ReferralItem['referrerType'];
  incomingReferrerUuid?: string;
  incomingReferralTime?: Dayjs;
  incomingIsPrimary?: boolean;
  incomingProfitShareRatio?: number;
  incomingRemark?: string;
};

type OpportunityActivityScheduleModalState = {
  mode: 'create' | 'edit';
  item?: CalendarEventItem;
};

type OpportunityAdvanceModalValues = {
  targetStageUuid?: string;
  lostReason?: string;
  lostReasonDetail?: string;
};

const OpportunityDetailDrawerContext =
  createContext<OpportunityDetailDrawerContextValue | null>(null);

const EMPTY_PAGE: OpportunityDrawerRelatedPage<never> = {
  list: [],
  total: 0,
};

const DEFAULT_SECTIONS: OpportunityDrawerSection[] = [
  { key: 'advance', label: '推进面板', enabled: true },
  { key: 'activities', label: '活动', enabled: true },
  { key: 'followUps', label: '跟进记录', enabled: true },
  { key: 'details', label: '详细资料', enabled: true },
  { key: 'teamMembers', label: '协作成员', enabled: true },
  { key: 'referrals', label: '介绍关系', enabled: true },
  { key: 'contacts', label: '联系人', enabled: true },
  { key: 'tasks', label: '任务', enabled: true },
  { key: 'businessTrips', label: '相关出差', enabled: true },
  { key: 'quotes', label: '报价单', enabled: true },
  { key: 'contracts', label: '合同', enabled: true },
  { key: 'products', label: '产品', enabled: true },
];

const normalizeText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeOptionalText = (value: unknown) =>
  normalizeText(value) || undefined;

const formatMoney = (value?: string | number | null) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0.00';
  return amount.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const boolTag = (value?: boolean) => (
  <Tag color={value ? 'green' : 'default'}>{value ? '是' : '否'}</Tag>
);

const opportunityTitle = (
  opportunity?: OpportunityItem,
  fallback?: ReactNode,
) => normalizeText(opportunity?.name) || fallback || '未命名商机';

const pageHint = (
  page: OpportunityDrawerRelatedPage<unknown>,
  label: string,
  pageSize = 5,
) =>
  page.total > pageSize
    ? `已展示最近 ${pageSize} 条，共 ${page.total} 条${label}`
    : `共 ${page.total} 条${label}`;

const renderReferralObject = (
  type?: ReferralItem['referrerType'] | ReferralItem['targetType'],
  uuid?: string,
  name?: string,
) => {
  const label = name || uuid || '-';
  if (!uuid) {
    return <Text type="secondary">{label}</Text>;
  }
  if (type === 'contact') {
    return <ContactReferenceLink contactUuid={uuid} contactName={label} />;
  }
  if (type === 'customer') {
    return <CustomerReferenceLink customerUuid={uuid} customerName={label} />;
  }
  if (type === 'lead') {
    return <LeadReferenceLink leadUuid={uuid} leadName={label} />;
  }
  if (type === 'opportunity') {
    return (
      <OpportunityReferenceLink
        opportunityUuid={uuid}
        opportunityName={label}
      />
    );
  }
  return <Text>{label}</Text>;
};

const buildOpportunityIncomingReferralFormValues = (
  record?: ReferralItem,
): OpportunityIncomingReferralFormValues => ({
  incomingReferrerType: record?.referrerType,
  incomingReferrerUuid: normalizeOptionalText(record?.referrerUuid),
  incomingReferralTime: record?.referralTime
    ? dayjs(record.referralTime)
    : undefined,
  incomingIsPrimary: Boolean(record?.isPrimary),
  incomingProfitShareRatio:
    record?.profitShareRatio === undefined
      ? undefined
      : Number(record.profitShareRatio),
  incomingRemark: normalizeOptionalText(record?.remark),
});

const mergeDrawerSections = (sections?: OpportunityDrawerSection[]) => {
  const providedSections = sections || [];
  const merged = new Map<string, OpportunityDrawerSection>();

  DEFAULT_SECTIONS.forEach((section) => {
    merged.set(section.key, section);
  });
  providedSections.forEach((section) => {
    merged.set(section.key, section);
  });

  if (!merged.has('advance')) {
    merged.set('advance', {
      key: 'advance',
      label: '推进面板',
      enabled: true,
    });
  }

  return normalizeDetailSections('opportunity', Array.from(merged.values()));
};

const toIsoValue = (value?: Dayjs | null) => toApiDateTime(value);

function MetaItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.metaItem}>
      <span>{label}</span>
      <strong>{value || '-'}</strong>
    </div>
  );
}

function EmptyBlock({ description }: { description: string }) {
  return (
    <div className={styles.emptyBlock}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
    </div>
  );
}

function DetailAccessError({ onRetry }: { onRetry: () => void }) {
  return (
    <DetailSurface className={styles.sectionCard}>
      <div className={styles.loading}>
        <Alert
          type="error"
          showIcon
          message="当前记录不可访问或已不存在"
          description="详情暂时无法打开，你可以关闭弹窗后返回列表确认记录状态。"
          action={
            <Space>
              <Button onClick={onRetry}>重试</Button>
            </Space>
          }
        />
      </div>
    </DetailSurface>
  );
}

function SectionPanel({ title, hint, extra, children }: SectionPanelProps) {
  return (
    <DetailSurface
      title={title}
      extra={
        <Space size={8}>
          {hint ? <Text type="secondary">{hint}</Text> : undefined}
          {extra}
        </Space>
      }
      className={styles.sectionCard}
    >
      {children}
    </DetailSurface>
  );
}

export function useOpportunityDetailDrawer() {
  const ctx = useContext(OpportunityDetailDrawerContext);
  return (
    ctx || {
      openOpportunity: () => undefined,
    }
  );
}

export function OpportunityDetailDrawerProvider({
  children,
}: OpportunityDetailDrawerProviderProps) {
  const [selectedViewMode, setSelectedViewMode] =
    useState<OpportunityViewMode>();
  const [open, setOpen] = useState(false);
  const [opportunityUuid, setOpportunityUuid] = useState<string>();
  const [fallbackName, setFallbackName] = useState<ReactNode>();

  const openOpportunity = useCallback((input: OpportunityDrawerOpenInput) => {
    const uuid = normalizeText(input.opportunityUuid);
    if (!uuid) {
      message.warning('该记录缺少商机 UUID，无法打开商机详情');
      return;
    }
    setOpportunityUuid(uuid);
    setSelectedViewMode(input.viewMode);
    setFallbackName(input.opportunityName);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openOpportunity }), [openOpportunity]);

  return (
    <OpportunityDetailDrawerContext.Provider value={value}>
      {children}
      <OpportunityDetailDrawer
        viewMode={selectedViewMode}
        open={open}
        opportunityUuid={opportunityUuid}
        fallbackName={fallbackName}
        onClose={() => setOpen(false)}
      />
    </OpportunityDetailDrawerContext.Provider>
  );
}

export function OpportunityReferenceLink({
  viewMode,
  opportunityUuid,
  opportunityName,
  fallbackName,
  emptyText = '-',
  canViewDetail = true,
  style,
}: OpportunityReferenceLinkProps) {
  const { openOpportunity } = useOpportunityDetailDrawer();
  const label = opportunityName || fallbackName || emptyText;
  if (!normalizeText(opportunityUuid) || canViewDetail === false) {
    const content = (
      <Text type="secondary" style={style}>
        {label}
      </Text>
    );
    if (normalizeText(opportunityUuid) && canViewDetail === false) {
      return <Tooltip title="无详情权限">{content}</Tooltip>;
    }
    return content;
  }
  return (
    <Button
      type="link"
      className={styles.opportunityLink}
      style={style}
      onClick={() =>
        openOpportunity({ opportunityUuid, opportunityName: label, viewMode })
      }
    >
      {label}
    </Button>
  );
}

export function OpportunityDetailDrawer({
  viewMode: selectedViewMode,
  open,
  opportunityUuid,
  fallbackName,
  onClose,
}: OpportunityDetailDrawerProps) {
  const currentPathname = history.location?.pathname || '';
  const pageViewMode: OpportunityViewMode =
    selectedViewMode ||
    (currentPathname.includes('/opportunity/followed') ||
    (currentPathname.endsWith('/overview') &&
      new URLSearchParams(history.location?.search || '').get('viewMode') ===
        'followed')
      ? 'followed'
      : resolveCrmScopedViewModeFromPathname(
          currentPathname + (history.location?.search || ''),
          'opportunities',
          {
            allowPool: true,
          },
        ));
  const access = useAccess() as Record<string, boolean>;
  const [convertOpen, setConvertOpen] = useState(false);
  const [profile, setProfile] = useState<OpportunityDrawerProfile>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [activeSection, setActiveSection] =
    useState<OpportunityDrawerSectionKey>('advance');
  const [lookups, setLookups] = useState<OpportunityDrawerLookups>({
    customerOptions: [],
    memberOptions: [],
    pipelineOptions: [],
    stageOptions: [],
  });
  const [quickFollowUpOpen, setQuickFollowUpOpen] = useState(false);
  const [quickFollowUpSubmitting] = useState(false);
  const [quickFollowUpDefaults, setQuickFollowUpDefaults] = useState<
    Record<string, unknown>
  >({});
  const [opportunityEditOpen, setOpportunityEditOpen] = useState(false);
  const [lifecycleTarget, setLifecycleTarget] = useState<
    'active' | 'on_hold' | 'closed'
  >();
  const [editingFollowUp, setEditingFollowUp] =
    useState<OpportunityDrawerFollowUpItem>();
  const [scheduleForm] = Form.useForm<CalendarEventFormValues>();
  const [advanceForm] = Form.useForm<OpportunityAdvanceModalValues>();
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const [advanceSubmitting, setAdvanceSubmitting] = useState(false);
  const [scheduleEditor, setScheduleEditor] =
    useState<OpportunityActivityScheduleModalState | null>(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [relatedMemberOptions, setRelatedMemberOptions] = useState<
    RelationOption[]
  >([]);
  const [relatedMemberLoading, setRelatedMemberLoading] = useState(false);
  const [relatedMemberLoadFailed, setRelatedMemberLoadFailed] = useState(false);
  const latestProfileRequestRef = useRef(0);
  const previousOpportunityUuidRef = useRef<string | undefined>(undefined);
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);
  const handleFollowUpTotalChange = useCallback((total: number) => {
    setProfile((current) => {
      if (!current) return current;
      return {
        ...current,
        sections: mergeDrawerSections(current.sections).map((section) =>
          section.key === 'followUps' ? { ...section, count: total } : section,
        ),
      };
    });
  }, []);
  useEffect(() => {
    if (!open) {
      previousOpportunityUuidRef.current = undefined;
      return;
    }
    setProfile(undefined);
    setError(undefined);
  }, [open, opportunityUuid]);

  const loadProfile = useCallback(
    async (notifyProjectionFailure = false) => {
      const targetUuid = normalizeText(opportunityUuid);
      if (!open || !targetUuid) return;
      const requestId = latestProfileRequestRef.current + 1;
      latestProfileRequestRef.current = requestId;
      setLoading(true);
      setError(undefined);
      try {
        const projection = await refreshOpportunityProjections(
          targetUuid,
        ).catch(() => undefined);
        const data = await queryOpportunityDrawerProfile(targetUuid, {
          viewMode: pageViewMode,
        });
        if (requestId !== latestProfileRequestRef.current) {
          return;
        }
        if (notifyProjectionFailure && !projection?.followUpAvailable) {
          message.warning('最近联系时间同步失败，请稍后重试');
        }
        setProfile(
          projection
            ? {
                ...data,
                opportunity: {
                  ...data.opportunity,
                  contactLastTime: projection.followUpAvailable
                    ? projection.contactLastTime || undefined
                    : data.opportunity.contactLastTime,
                  nextActionTask: projection.nextActionTask,
                  nextActionAvailable: projection.nextActionAvailable,
                },
              }
            : data,
        );
      } catch (e) {
        if (requestId !== latestProfileRequestRef.current) {
          return;
        }
        const err = e instanceof Error ? e : new Error('商机详情加载失败');
        setError(err);
        setProfile(undefined);
        message.error(err.message);
      } finally {
        if (requestId === latestProfileRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [open, opportunityUuid, pageViewMode],
  );

  const { memberOptions: sharedMemberOptions, searchMembers } =
    useMemberDirectory();
  const opportunity = profile?.opportunity;
  const canEditOpportunity = Boolean(
    opportunity?.conversionEditScopes?.some((scope) => access[scope]),
  );
  const effectiveRecordViewMode: CrmScopedViewMode =
    pageViewMode === 'followed'
      ? opportunity?.followSourceViewMode || 'all'
      : pageViewMode;
  const summary = profile?.summary;

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!open) return;
    void Promise.all([
      fetchCustomerOptions().then((customerOptions) =>
        setLookups((prev) => ({ ...prev, customerOptions })),
      ),
      Promise.resolve(sharedMemberOptions).then((memberOptions) =>
        setLookups((prev) => ({ ...prev, memberOptions })),
      ),
      fetchPipelineOptions().then((pipelineOptions) =>
        setLookups((prev) => ({ ...prev, pipelineOptions })),
      ),
      fetchStageOptions().then((stageOptions) =>
        setLookups((prev) => ({ ...prev, stageOptions })),
      ),
    ]);
  }, [open, sharedMemberOptions]);

  const loadRelatedMembers = useCallback(
    async (keyword?: string) => {
      setRelatedMemberLoading(true);
      try {
        const options = await searchMembers(keyword);
        setRelatedMemberOptions(options);
        setRelatedMemberLoadFailed(false);
        return options;
      } catch {
        setRelatedMemberLoadFailed(true);
        message.error('成员列表加载失败，请稍后重试');
        return [];
      } finally {
        setRelatedMemberLoading(false);
      }
    },
    [searchMembers],
  );

  const openScheduleCreate = useCallback(() => {
    if (!opportunity?.uuid) {
      return;
    }
    scheduleForm.resetFields();
    scheduleForm.setFieldsValue(
      calendarEventFormDefaultValues({
        title: '',
        eventType: 'my_schedule',
        startTime: dayjs(),
        endTime: dayjs().add(1, 'hour'),
        allDay: false,
        ownerMemberUuid: normalizeOptionalText(opportunity?.ownerUserUuid),
        participantMemberUuids: [],
        relations: [
          createCalendarRelationFormValue('opportunity', opportunity.uuid),
        ],
      }),
    );
    void loadRelatedMembers();
    setScheduleEditor({ mode: 'create' });
  }, [
    loadRelatedMembers,
    opportunity?.ownerUserUuid,
    opportunity?.uuid,
    scheduleForm,
  ]);

  const _openScheduleEdit = useCallback(async () => {}, []);

  const closeScheduleEditor = useCallback(() => {
    setScheduleEditor(null);
    setScheduleSubmitting(false);
    scheduleForm.resetFields();
  }, [scheduleForm]);

  const submitScheduleEditor = useCallback(async () => {
    if (!opportunity?.uuid || !scheduleEditor) {
      return;
    }
    const values = await scheduleForm.validateFields();
    setScheduleSubmitting(true);
    try {
      const startTime = toApiDateTime(values.startTime);
      if (!startTime) {
        throw new Error('invalid start time');
      }
      const payload = {
        title: normalizeText(values.title),
        eventType: normalizeOptionalText(values.eventType) || 'my_schedule',
        startTime,
        endTime: toApiDateTime(values.endTime),
        allDay: Boolean(values.allDay),
        location: normalizeOptionalText(values.location),
        description: normalizeOptionalText(values.description),
        ownerMemberUuid: normalizeOptionalText(values.ownerMemberUuid),
        participantMemberUuids: Array.isArray(values.participantMemberUuids)
          ? (values.participantMemberUuids
              .map((item) => normalizeOptionalText(item))
              .filter(Boolean) as string[])
          : [],
        relations: normalizeCalendarRelationPayload(values.relations) || [
          {
            bizType: 'opportunity' as const,
            bizUuid: opportunity.uuid,
            relationRole: 'primary' as const,
          },
        ],
      };

      if (scheduleEditor.mode === 'edit' && scheduleEditor.item?.uuid) {
        await updateCalendarEvent(scheduleEditor.item.uuid, payload);
        message.success('日程已更新');
      } else {
        await createCalendarEvent(payload);
        message.success('日程已创建');
      }
      closeScheduleEditor();
      await loadProfile();
    } catch (error) {
      console.error(error);
      message.error('保存失败，请稍后重试');
    } finally {
      setScheduleSubmitting(false);
    }
  }, [
    closeScheduleEditor,
    loadProfile,
    opportunity?.uuid,
    scheduleEditor,
    scheduleForm,
  ]);

  const sections = useMemo(
    () =>
      mergeDrawerSections(profile?.sections).map((section) =>
        section.key === 'followUps'
          ? {
              ...section,
              count: section.count ?? profile?.related?.followUps?.total ?? 0,
            }
          : section,
      ),
    [profile?.sections, profile?.related?.followUps?.total],
  );

  const displayedSection =
    sections.find((section) => section.key === activeSection)?.key ||
    defaultDetailSection('opportunity', sections);
  useEffect(() => {
    const currentOpportunityUuid = normalizeText(profile?.opportunity?.uuid);
    if (!currentOpportunityUuid) {
      return;
    }

    const sectionList = sections;
    const sectionKeys = new Set(
      sectionList.map((section) => section.key as OpportunityDrawerSectionKey),
    );
    const fallbackSection =
      (defaultDetailSection(
        'opportunity',
        sectionList,
      ) as OpportunityDrawerSectionKey) || 'advance';
    const isOpportunityChanged =
      previousOpportunityUuidRef.current !== currentOpportunityUuid;

    if (isOpportunityChanged) {
      previousOpportunityUuidRef.current = currentOpportunityUuid;
      setActiveSection(fallbackSection);
      return;
    }

    if (sectionKeys.size === 0) {
      return;
    }

    if (!sectionKeys.has(activeSection)) {
      setActiveSection(fallbackSection);
    }
  }, [activeSection, profile?.opportunity?.uuid, sections]);

  const relationContext = useMemo<RelationCreateContext>(
    () => ({
      customerUuid: opportunity?.customerUuid,
      customerName: opportunity?.customerName,
      opportunityUuid: opportunity?.uuid,
      opportunityName: opportunity?.name,
    }),
    [
      opportunity?.customerName,
      opportunity?.customerUuid,
      opportunity?.name,
      opportunity?.uuid,
    ],
  );

  const stageProgress = profile?.stageProgress;
  const stageList = stageProgress?.stages || [];
  const currentStageIndex = stageList.findIndex(
    (item) => item.uuid === stageProgress?.currentStageUuid,
  );
  const availableForwardStages =
    currentStageIndex >= 0 ? stageList.slice(currentStageIndex + 1) : [];
  const stageByUuid = new Map(stageList.map((item) => [item.uuid, item]));
  // 赢单/输单由阶段派生，已在终态阶段（赢单/输单）的商机不再允许推进。
  const opportunityOutcome = resolveOpportunityOutcome(
    opportunity,
    currentStageIndex >= 0 ? stageList[currentStageIndex] : undefined,
  );
  const opportunityEnded = opportunityOutcome !== 'ongoing';

  const openAdvanceModal = useCallback(() => {
    if (!opportunity?.uuid || opportunityEnded) {
      return;
    }
    const defaultStageUuid =
      availableForwardStages.length === 1
        ? availableForwardStages[0].uuid
        : undefined;
    advanceForm.setFieldsValue({
      targetStageUuid: defaultStageUuid,
      lostReason: undefined,
      lostReasonDetail: undefined,
    });
    setAdvanceModalOpen(true);
  }, [
    advanceForm,
    availableForwardStages,
    opportunity?.uuid,
    opportunityEnded,
  ]);

  const closeAdvanceModal = useCallback(() => {
    setAdvanceModalOpen(false);
    setAdvanceSubmitting(false);
    advanceForm.setFieldsValue({
      targetStageUuid: undefined,
      lostReason: undefined,
      lostReasonDetail: undefined,
    });
  }, [advanceForm]);

  const submitAdvanceModal = useCallback(async () => {
    if (!opportunity?.uuid) {
      message.error('缺少当前商机信息，无法推进');
      return;
    }
    const values = await advanceForm.validateFields();
    const targetStageUuid = normalizeText(values.targetStageUuid);
    if (!targetStageUuid) {
      message.error('请选择目标阶段');
      return;
    }
    const payload: UpdateOpportunityPayload = { stageUuid: targetStageUuid };
    // 推进到输单类阶段时必须留下归因，否则仪表盘的输单原因排行永远无数据。
    if (
      resolveOpportunityOutcomeFromStage(stageByUuid.get(targetStageUuid)) ===
      'lost'
    ) {
      payload.lostReason = normalizeOptionalText(values.lostReason);
      payload.lostReasonDetail = normalizeOptionalText(values.lostReasonDetail);
    }
    setAdvanceSubmitting(true);
    try {
      await updateOpportunity(opportunity.uuid, payload);
      message.success('商机阶段已更新');
      closeAdvanceModal();
      await loadProfile();
    } catch (error) {
      const nextError =
        error instanceof Error ? error : new Error('商机推进失败，请稍后重试');
      message.error(nextError.message);
    } finally {
      setAdvanceSubmitting(false);
    }
  }, [advanceForm, closeAdvanceModal, loadProfile, opportunity?.uuid]);
  return (
    <BusinessModal
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      title={null}
      detail
      aria-label="商机详情"
    >
      <div className={styles.shell} data-crm-shell>
        <DetailSurface className={styles.heroCard}>
          <div className={styles.hero} data-crm-hero>
            <div className={styles.heroMain}>
              <div className={styles.heroTopRow} data-crm-hero-row>
                <div>
                  <div className={styles.titleRow} data-crm-title-row>
                    <Title level={3} className={styles.title} data-crm-title>
                      {opportunityTitle(opportunity, fallbackName)}
                    </Title>
                    {opportunity?.uuid ? (
                      <OpportunityFollowButton
                        opportunityUuid={opportunity.uuid}
                        isFollowed={opportunity.isFollowed}
                        viewMode={effectiveRecordViewMode}
                        onChange={(isFollowed) =>
                          setProfile((current) =>
                            current
                              ? {
                                  ...current,
                                  opportunity: {
                                    ...current.opportunity,
                                    isFollowed,
                                  },
                                }
                              : current,
                          )
                        }
                      />
                    ) : null}
                  </div>
                  <Space wrap size={[8, 8]} className={styles.heroTags}>
                    <Tag color="blue">
                      当前阶段{' '}
                      {resolveRelationLabel(
                        opportunity?.stageUuid,
                        lookups.stageOptions,
                        opportunity?.stageName,
                      )}
                    </Tag>
                    <Tag>
                      跟进状态{' '}
                      {String(opportunity?.followUpStatus ?? '-').replace(
                        'true',
                        '已跟进',
                      )}
                    </Tag>
                    {opportunityEnded ? (
                      <Tag
                        color={resolveOpportunityOutcomeColor(
                          opportunityOutcome,
                        )}
                      >
                        {resolveOpportunityOutcomeLabel(opportunityOutcome)}
                      </Tag>
                    ) : null}
                  </Space>
                </div>
                <Space wrap>
                  {opportunity?.convertedLeadUuid ? (
                    <LeadReferenceLink
                      leadUuid={opportunity.convertedLeadUuid}
                      leadName="查看已转换线索"
                    />
                  ) : opportunity?.uuid ? (
                    <Tooltip
                      title={
                        opportunity.conversionBlockedReason ||
                        (!access['leads-my:create']
                          ? '无新建线索权限'
                          : !canEditOpportunity
                            ? '无商机编辑权限'
                            : undefined)
                      }
                    >
                      <span>
                        <Button
                          disabled={
                            Boolean(opportunity.conversionBlockedReason) ||
                            !access['leads-my:create'] ||
                            !canEditOpportunity
                          }
                          onClick={() => setConvertOpen(true)}
                        >
                          转为线索
                        </Button>
                      </span>
                    </Tooltip>
                  ) : null}
                  {opportunity?.uuid &&
                  opportunity.recordPermission?.actions?.edit !== false ? (
                    <>
                      {opportunityOutcome === 'ongoing' ? (
                        <>
                          <Button onClick={() => setLifecycleTarget('on_hold')}>
                            搁置
                          </Button>
                          <Button onClick={() => setLifecycleTarget('closed')}>
                            关闭商机
                          </Button>
                        </>
                      ) : null}
                      {opportunityOutcome === 'on_hold' ? (
                        <Button onClick={() => setLifecycleTarget('on_hold')}>
                          调整搁置
                        </Button>
                      ) : null}
                      {!opportunity?.convertedLeadUuid &&
                      (opportunityOutcome === 'on_hold' ||
                        opportunityOutcome === 'closed') ? (
                        <Button onClick={() => setLifecycleTarget('active')}>
                          恢复跟进
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                  {opportunity?.uuid ? (
                    <Button
                      type="primary"
                      onClick={() => setOpportunityEditOpen(true)}
                    >
                      编辑
                    </Button>
                  ) : null}
                </Space>
              </div>
              {opportunity?.convertedLeadUuid ? (
                <Alert
                  type="info"
                  showIcon
                  title="已转为线索，请通过关联线索继续跟进"
                  description={
                    <Space wrap>
                      <span>{opportunity.convertedToLeadReason}</span>
                      <span>
                        {formatCrmDate(opportunity.convertedToLeadAt)}
                      </span>
                    </Space>
                  }
                />
              ) : null}
              {opportunity?.lifecycleStatus &&
              opportunity.lifecycleStatus !== 'active' ? (
                <Alert
                  type={
                    opportunity.lifecycleStatus === 'on_hold'
                      ? 'warning'
                      : 'info'
                  }
                  showIcon
                  title={
                    opportunity.lifecycleStatus === 'on_hold'
                      ? '已搁置，等待复查'
                      : '已关闭（未成交）'
                  }
                  description={
                    <>
                      <div>原因：{opportunity.statusReason || '-'}</div>
                      {opportunity.lifecycleStatus === 'on_hold' ? (
                        <div>
                          复查日期：
                          {formatCrmDate(opportunity.reviewAt, 'date')}
                          {opportunity.reviewAt &&
                          dayjs(opportunity.reviewAt).isBefore(dayjs(), 'day')
                            ? '（已到期，请复查）'
                            : ''}
                        </div>
                      ) : (
                        <div>
                          关闭日期：
                          {formatCrmDate(opportunity.closedAt, 'dateTime')}
                        </div>
                      )}
                    </>
                  }
                />
              ) : null}
              <div className={styles.metaGrid} data-crm-meta>
                <MetaItem
                  label="客户名称"
                  value={
                    <CustomerReferenceLink
                      customerUuid={opportunity?.customerUuid}
                      customerName={resolveRelationLabel(
                        opportunity?.customerUuid,
                        lookups.customerOptions,
                        opportunity?.customerName,
                      )}
                    />
                  }
                />
                <MetaItem
                  label="商机金额（元）"
                  value={formatMoney(
                    summary?.totalPrice || opportunity?.totalPrice,
                  )}
                />
                <MetaItem
                  label="负责人"
                  value={resolveRelationText({
                    value: opportunity?.ownerUserUuid,
                    options: lookups.memberOptions,
                    emptyText: opportunity?.ownerUserUuid || '-',
                  })}
                />
                <MetaItem
                  label="最近联系"
                  value={formatCrmDate(opportunity?.contactLastTime, 'date')}
                />
              </div>
            </div>
          </div>
        </DetailSurface>
        <div className={styles.workspace} data-crm-workspace>
          <aside className={styles.navPanel} data-crm-nav>
            <DetailSurface
              className={styles.navCard}
              data-crm-nav-surface
              title="工作视角"
            >
              <DetailNavigation
                selectedKeys={displayedSection ? [displayedSection] : []}
                className={styles.navMenu}
                items={sections.map((section) => ({
                  key: section.key,
                  label: (
                    <div className={styles.menuLabel}>
                      <span>{section.label}</span>
                      {typeof section.count === 'number' &&
                      section.count > 0 ? (
                        <Text type="secondary">{section.count}</Text>
                      ) : null}
                    </div>
                  ),
                }))}
                onClick={(info) =>
                  setActiveSection(info.key as OpportunityDrawerSectionKey)
                }
              />
            </DetailSurface>
          </aside>

          <div className={styles.mainPanel} data-crm-main>
            <div className={styles.panelStack} data-crm-stack>
              {loading && !profile ? (
                <DetailSurface className={styles.sectionCard}>
                  <div className={styles.loading}>
                    <Skeleton active paragraph={{ rows: 8 }} />
                  </div>
                </DetailSurface>
              ) : error ? (
                <DetailAccessError onRetry={() => void loadProfile()} />
              ) : profile && displayedSection ? (
                renderSection(
                  displayedSection,
                  profile,
                  effectiveRecordViewMode,
                  lookups,
                  relationContext,
                  openAdvanceModal,
                  () => {
                    setQuickFollowUpDefaults({});
                    setEditingFollowUp(undefined);
                    setQuickFollowUpOpen(true);
                  },
                  (record) => {
                    setQuickFollowUpOpen(false);
                    setQuickFollowUpDefaults({});
                    setEditingFollowUp(record);
                  },
                  openScheduleCreate,
                  scheduleForm,
                  scheduleEditor,
                  scheduleSubmitting,
                  relatedMemberOptions,
                  relatedMemberLoading,
                  relatedMemberLoadFailed,
                  loadRelatedMembers,
                  closeScheduleEditor,
                  submitScheduleEditor,
                  handleFollowUpTotalChange,
                  () => loadProfile(true),
                  sections.map((section) => section.key),
                  setActiveSection,
                )
              ) : (
                <DetailSurface className={styles.sectionCard}>
                  <EmptyBlock
                    description={
                      profile ? '当前无可查看的分区' : '暂无商机详情数据'
                    }
                  />
                </DetailSurface>
              )}
            </div>
          </div>

          <div className={styles.summaryPanel} data-crm-summary>
            <AiDetailPanel
              bizType="opportunity"
              bizUuid={opportunity?.uuid || ''}
              initialSummary={profile?.summaryPanel}
              summaryCounts={summary}
              activeTab="summary"
              contextTitle={profile?.opportunity?.name}
            />
          </div>
        </div>
      </div>
      <FollowUpCreateModal
        open={quickFollowUpOpen || Boolean(editingFollowUp)}
        title={editingFollowUp ? '编辑跟进记录' : '新建跟进记录'}
        onCancel={() => {
          setQuickFollowUpOpen(false);
          setEditingFollowUp(undefined);
          setQuickFollowUpDefaults({});
        }}
        confirmLoading={quickFollowUpSubmitting}
        context={{
          bizType: 4,
          bizUuid: opportunity?.uuid || '',
          bizName: opportunity?.name,
          defaultOpportunityUuids: opportunity?.uuid
            ? [opportunity.uuid]
            : undefined,
        }}
        editTarget={
          editingFollowUp ? { uuid: editingFollowUp.uuid } : undefined
        }
        defaultValues={
          editingFollowUp
            ? {
                followUpType: editingFollowUp.followUpType,
                content: editingFollowUp.content,
                nextTime: editingFollowUp.nextTime
                  ? dayjs(editingFollowUp.nextTime)
                  : undefined,
                qualityScore: editingFollowUp.qualityScore,
                stageAdvanced: editingFollowUp.stageAdvanced,
                nextStepConfirmed: editingFollowUp.nextStepConfirmed,
                nextStepContent: editingFollowUp.nextStepContent,
                nextStepAt: editingFollowUp.nextStepAt
                  ? dayjs(editingFollowUp.nextStepAt)
                  : undefined,
                keySignal: editingFollowUp.keySignal,
                lostReasonHint: editingFollowUp.lostReasonHint,
                syncOpportunityAdvance: editingFollowUp.syncOpportunityAdvance,
              }
            : quickFollowUpDefaults
        }
        onSuccess={async () => {
          setQuickFollowUpOpen(false);
          setEditingFollowUp(undefined);
          setQuickFollowUpDefaults({});
          await loadProfile(true);
        }}
      />
      {convertOpen && opportunity ? (
        <OpportunityToLeadModal
          opportunityUuid={opportunity.uuid}
          onCancel={() => setConvertOpen(false)}
          onSuccess={() => {
            setConvertOpen(false);
            notifyOpportunityConvertedToLead();
            void loadProfile();
          }}
        />
      ) : null}
      {lifecycleTarget && opportunity ? (
        <OpportunityLifecycleModal
          opportunity={opportunity}
          status={lifecycleTarget}
          onCancel={() => setLifecycleTarget(undefined)}
          onSuccess={() => {
            setLifecycleTarget(undefined);
            void loadProfile();
          }}
        />
      ) : null}
      <OpportunityEditModal
        open={opportunityEditOpen}
        onOpenChange={setOpportunityEditOpen}
        opportunityUuid={opportunity?.uuid}
        currentMemberUuid={undefined}
        zIndex={1100}
        onSuccess={() => {
          setOpportunityEditOpen(false);
          void loadProfile();
        }}
      />
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={advanceModalOpen}
        title="推进商机"
        onCancel={closeAdvanceModal}
        onOk={() => void submitAdvanceModal()}
        confirmLoading={advanceSubmitting}
        destroyOnHidden
      >
        <Form form={advanceForm} layout="vertical">
          <Form.Item
            name="targetStageUuid"
            label="目标阶段"
            rules={[{ required: true, message: '请选择目标阶段' }]}
          >
            <Select
              options={availableForwardStages.map((item) => ({
                label: item.label,
                value: item.uuid,
              }))}
              placeholder="请选择后续阶段"
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.targetStageUuid !== next.targetStageUuid
            }
          >
            {({ getFieldValue }) => {
              const targetStage = stageByUuid.get(
                normalizeText(getFieldValue('targetStageUuid')) || '',
              );
              // 目标是输单类阶段时必须留归因，否则仪表盘的输单原因排行永远无数据。
              if (resolveOpportunityOutcomeFromStage(targetStage) !== 'lost') {
                return null;
              }
              return (
                <>
                  <Form.Item
                    name="lostReason"
                    label="输单原因"
                    rules={[{ required: true, message: '请输入输单原因' }]}
                  >
                    <Input maxLength={255} placeholder="请输入原因" />
                  </Form.Item>
                  <Form.Item name="lostReasonDetail" label="输单说明">
                    <Input.TextArea rows={3} maxLength={500} />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </BusinessModal>
  );
}

function renderSection(
  key: OpportunityDrawerSectionKey,
  profile: OpportunityDrawerProfile,
  pageViewMode: CrmScopedViewMode,
  lookups: OpportunityDrawerLookups,
  relationContext: RelationCreateContext,
  onAdvance: () => void,
  onCreateFollowUp: () => void,
  _onEditFollowUp: (record: OpportunityDrawerFollowUpItem) => void,
  _onScheduleCreate: () => void,
  _scheduleForm: ReturnType<typeof Form.useForm<CalendarEventFormValues>>[0],
  _scheduleEditor: OpportunityActivityScheduleModalState | null,
  _scheduleSubmitting: boolean,
  _relatedMemberOptions: RelationOption[],
  _relatedMemberLoading: boolean,
  _relatedMemberLoadFailed: boolean,
  _onSearchRelatedMembers: (keyword?: string) => void,
  _onCloseScheduleEditor: () => void,
  _onSubmitScheduleEditor: () => void,
  onFollowUpTotalChange: (total: number) => void,
  onRefresh: () => void,
  visibleSectionKeys: readonly string[],
  onNavigate: (key: 'tasks' | 'followUps') => void,
) {
  const related =
    profile.related || ({} as OpportunityDrawerProfile['related']);
  const contacts = related.contacts || EMPTY_PAGE;
  const quotes = related.quotes || EMPTY_PAGE;
  const contracts = related.contracts || EMPTY_PAGE;
  const products = related.products || EMPTY_PAGE;
  const referralsIn = related.referralsIn || EMPTY_PAGE;
  const refreshAfterScoreRelevantChange = () => {
    void touchOpportunity(profile.opportunity.uuid)
      .catch(() => undefined)
      .then(() => onRefresh());
  };

  switch (key) {
    case 'advance':
      return (
        <OpportunityAdvanceSummary
          key={profile.opportunity.uuid}
          profile={profile}
          memberOptions={lookups.memberOptions}
          visibleSectionKeys={visibleSectionKeys}
          onAdvance={onAdvance}
          onCreateFollowUp={onCreateFollowUp}
          onNavigate={onNavigate}
        />
      );
    case 'activities':
      return (
        <SectionPanel title="活动">
          <ActivityPanel
            bizType="opportunity"
            bizUuid={profile.opportunity.uuid}
            bizName={profile.opportunity.name}
          />
        </SectionPanel>
      );
    case 'followUps':
      return (
        <SectionPanel title="跟进记录">
          <ActivityPanelFollowUps
            bizType="opportunity"
            bizUuid={profile.opportunity.uuid}
            bizName={profile.opportunity.name}
            onTotalChange={onFollowUpTotalChange}
            onRecordSaved={onRefresh}
          />
        </SectionPanel>
      );
    case 'details':
      return <DetailsSection profile={profile} lookups={lookups} />;
    case 'teamMembers':
      return (
        <TeamMembersPanel
          bizType="opportunity"
          bizUuid={profile.opportunity.uuid}
          viewMode={pageViewMode}
          onRefresh={onRefresh}
        />
      );
    case 'referrals':
      return (
        <ReferralSection
          opportunityUuid={profile.opportunity.uuid}
          page={referralsIn}
          onRefresh={onRefresh}
        />
      );
    case 'contacts':
      return (
        <ContactSection
          page={contacts}
          relationContext={relationContext}
          onRefresh={refreshAfterScoreRelevantChange}
        />
      );
    case 'tasks':
      return (
        <CrmTaskRelationPanel
          title={detailSectionLabel('tasks')}
          sourceSceneUuid="opportunity"
          sourceObjectUuid={profile.opportunity.uuid}
          className={styles.sectionCard}
          onTasksChanged={() =>
            touchOpportunity(profile.opportunity.uuid)
              .then(() =>
                refreshOpportunityProjections(profile.opportunity.uuid),
              )
              .then(() => onRefresh())
              .catch(() => undefined)
          }
        />
      );
    case 'businessTrips':
      return (
        <BusinessTripRelationPanel
          title={detailSectionLabel('businessTrips')}
          objectType="opportunity"
          objectUuid={profile.opportunity.uuid}
        />
      );
    case 'quotes':
      return (
        <QuoteSection
          page={quotes}
          relationContext={relationContext}
          ownerUserUuid={profile.opportunity.ownerUserUuid}
          onRefresh={refreshAfterScoreRelevantChange}
        />
      );
    case 'contracts':
      return (
        <ContractSection
          page={contracts}
          relationContext={relationContext}
          ownerUserUuid={profile.opportunity.ownerUserUuid}
          onRefresh={refreshAfterScoreRelevantChange}
        />
      );
    case 'products':
      return <ProductSection page={products} />;
    default:
      return null;
  }
}

function DetailsSection({
  profile,
  lookups,
}: {
  profile: OpportunityDrawerProfile;
  lookups: OpportunityDrawerLookups;
}) {
  const opportunity = profile.opportunity;

  return (
    <SectionPanel title={detailSectionLabel('details')}>
      <ProDescriptions column={{ xs: 1, md: 2 }} dataSource={opportunity}>
        <ProDescriptions.Item label="商机名称" dataIndex="name" />
        <ProDescriptions.Item label="标签" span={2}>
          <EntityTagEditor
            entityType="opportunity"
            entityUuid={opportunity.uuid}
            tags={opportunity.tags}
            editable={opportunity.recordPermission?.actions?.edit !== false}
          />
        </ProDescriptions.Item>
        <ProDescriptions.Item label="客户">
          <CustomerReferenceLink
            customerUuid={opportunity.customerUuid}
            customerName={resolveRelationLabel(
              opportunity.customerUuid,
              lookups.customerOptions,
              opportunity.customerName,
            )}
          />
        </ProDescriptions.Item>
        <ProDescriptions.Item label="负责人">
          {resolveRelationText({
            value: opportunity.ownerUserUuid,
            options: lookups.memberOptions,
            emptyText: opportunity.ownerUserUuid || '-',
          })}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="地区">
          {opportunity.primaryAreaName ||
            (Array.isArray(opportunity.areaNames)
              ? opportunity.areaNames.join('、')
              : '') ||
            '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="销售流程">
          {resolveRelationLabel(
            opportunity.pipelineUuid,
            lookups.pipelineOptions,
            opportunity.pipelineName,
          )}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="阶段">
          {resolveRelationLabel(
            opportunity.stageUuid,
            lookups.stageOptions,
            opportunity.stageName,
          )}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="阶段赢率">
          {opportunity.stageWinRate
            ? `${Number(opportunity.stageWinRate)}%`
            : '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="跟进状态">
          {boolTag(Boolean(opportunity.followUpStatus))}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="商机状态">
          {resolveOpportunityOutcomeLabel(
            opportunity.stageOutcome ||
              resolveOpportunityOutcome(opportunity, {
                stageType: opportunity.stageType,
              }),
          ) || '跟进中'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="产品总额">
          {formatMoney(profile.summary.totalProductPrice)} 元
        </ProDescriptions.Item>
        <ProDescriptions.Item label="商机金额">
          {formatMoney(profile.summary.totalPrice)} 元
        </ProDescriptions.Item>
        <ProDescriptions.Item label="最近联系">
          {formatCrmDate(opportunity.contactLastTime, 'date')}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="下次联系">
          {formatCrmDate(opportunity.contactNextTime, 'date')}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="商机优先级">
          {opportunityPriorityLabel(opportunity.priority)}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="下一行动">
          {opportunity.nextActionAvailable === false
            ? '任务中心暂不可用'
            : opportunity.nextActionTask?.title || '—'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="行动截止">
          {formatCrmDate(opportunity.nextActionTask?.dueTime)}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="当前阻塞点">
          {opportunity.currentBlockers || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="决策人状态">
          {opportunity.decisionMakerStatus || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="预算状态">
          {opportunity.budgetStatus || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="竞争情况" span={2}>
          {opportunity.competitorInfo || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="风险标签" span={2}>
          {profile.advanceStatus?.riskTags?.length
            ? profile.advanceStatus.riskTags.map((item) => (
                <Tag key={item}>{item}</Tag>
              ))
            : '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="预计成交日">
          {formatCrmDate(
            opportunity.expectedDealDate || opportunity.dealTime,
            'date',
          )}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="进入当前阶段时间">
          {formatCrmDate(opportunity.stageEnteredAt)}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="输单原因">
          {opportunity.lostReason || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="输单说明">
          {opportunity.lostReasonDetail || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="折扣率">
          {Number(profile.summary.discountPercent || 0)}%
        </ProDescriptions.Item>
        <ProDescriptions.Item label="备注" span={2}>
          {opportunity.remark || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="结束说明" span={2}>
          {opportunity.endRemark || '-'}
        </ProDescriptions.Item>
      </ProDescriptions>
    </SectionPanel>
  );
}

function ReferralSection({
  opportunityUuid,
  page,
  onRefresh,
}: {
  opportunityUuid: string;
  page: OpportunityDrawerRelatedPage<ReferralItem>;
  onRefresh: () => void;
}) {
  const [contactOptions, setContactOptions] = useState<RelationOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<RelationOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<OpportunityIncomingReferralFormValues>();

  const incomingReferrals = useMemo(
    () => page.list.filter((item) => item.targetType === 'opportunity'),
    [page.list],
  );

  const loadCustomerReferralOptions = useCallback(
    async (
      keyword?: string,
      ensureValues?: Array<{ value: string; label?: string }>,
    ) => {
      const options = await fetchCustomerOptions(keyword);
      const ensured = (ensureValues || [])
        .filter((item) => normalizeText(item.value))
        .map((item) => ({
          value: normalizeText(item.value),
          label: normalizeText(item.label) || normalizeText(item.value),
        }));
      setCustomerOptions(mergeOptions(options, ensured));
    },
    [],
  );

  const loadContactReferralOptions = useCallback(
    async (
      keyword?: string,
      ensureValues?: Array<{ value: string; label?: string }>,
    ) => {
      const options = await fetchContactOptions(keyword);
      const ensured = (ensureValues || [])
        .filter((item) => normalizeText(item.value))
        .map((item) => ({
          value: normalizeText(item.value),
          label: normalizeText(item.label) || normalizeText(item.value),
        }));
      setContactOptions(mergeOptions(options, ensured));
    },
    [],
  );

  const ensureCurrentOptions = useCallback(
    (record?: ReferralItem) => {
      void loadCustomerReferralOptions(
        undefined,
        incomingReferrals
          .filter((item) => item.referrerType === 'customer')
          .map((item) => ({
            value: normalizeText(item.referrerUuid),
            label: item.referrerName,
          }))
          .concat(
            record?.referrerType === 'customer'
              ? [
                  {
                    value: normalizeText(record.referrerUuid),
                    label: record.referrerName,
                  },
                ]
              : [],
          ),
      );
      void loadContactReferralOptions(
        undefined,
        incomingReferrals
          .filter((item) => item.referrerType === 'contact')
          .map((item) => ({
            value: normalizeText(item.referrerUuid),
            label: item.referrerName,
          }))
          .concat(
            record?.referrerType === 'contact'
              ? [
                  {
                    value: normalizeText(record.referrerUuid),
                    label: record.referrerName,
                  },
                ]
              : [],
          ),
      );
    },
    [
      incomingReferrals,
      loadContactReferralOptions,
      loadCustomerReferralOptions,
    ],
  );

  const submitEdit = useCallback(
    async (
      values: OpportunityIncomingReferralFormValues,
      mode: 'create' | 'edit',
      record?: ReferralItem,
    ) => {
      const incomingReferrerUuid = normalizeText(values.incomingReferrerUuid);
      if (!normalizeText(opportunityUuid)) {
        message.error('缺少当前商机信息，无法保存介绍关系');
        return;
      }
      if (!incomingReferrerUuid) {
        message.error('请选择介绍人');
        return;
      }
      if (values.incomingIsPrimary) {
        const primaryExists = incomingReferrals.some(
          (item) =>
            Boolean(item.isPrimary) &&
            normalizeText(item.uuid) !== normalizeText(record?.uuid),
        );
        if (primaryExists) {
          message.error('同一商机只能设置一个主介绍人');
          return;
        }
      }

      setSaving(true);
      try {
        const payload = {
          referrerType:
            values.incomingReferrerType as ReferralItem['referrerType'],
          referrerUuid: incomingReferrerUuid,
          targetType: 'opportunity' as const,
          targetUuid: opportunityUuid,
          referralTime: toIsoValue(values.incomingReferralTime),
          isPrimary: values.incomingIsPrimary ? 1 : 0,
          profitShareRatio: values.incomingProfitShareRatio,
          remark: normalizeOptionalText(values.incomingRemark),
        };
        if (mode === 'edit' && record?.uuid) {
          await updateReferral(record.uuid, payload);
        } else {
          await createReferral(payload);
        }
        message.success('介绍关系更新成功');
        onRefresh();
      } catch (error) {
        const nextError =
          error instanceof Error ? error : new Error('介绍关系保存失败');
        message.error(nextError.message);
      } finally {
        setSaving(false);
      }
    },
    [incomingReferrals, onRefresh, opportunityUuid],
  );

  const removeReferral = useCallback(
    (record: ReferralItem) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '确认删除介绍关系',
        content: `确认删除与${record.referrerName || record.referrerUuid}相关的介绍关系吗`,
        okText: '删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          if (!record.uuid) {
            return;
          }
          await deleteReferral(record.uuid);
          message.success('介绍关系删除成功');
          onRefresh();
        },
      });
    },
    [onRefresh],
  );

  const referralColumns = useMemo<ColumnsType<ReferralItem>>(
    () => [
      {
        title: '介绍人',
        dataIndex: 'referrerName',
        key: 'referrerName',
        width: 180,
        render: (_, item) =>
          renderReferralObject(
            item.referrerType,
            item.referrerUuid,
            item.referrerName,
          ),
      },
      {
        title: '介绍人类型',
        dataIndex: 'referrerType',
        key: 'referrerType',
        width: 110,
        render: (value: ReferralItem['referrerType']) =>
          value === 'contact' ? '联系人' : '客户',
      },
      {
        title: '介绍时间',
        key: 'referralTime',
        dataIndex: 'referralTime',
        width: 150,
        render: (_, item) =>
          formatCrmDate(item.referralTime || item.createTime) || '-',
      },
      {
        title: '是否主介绍人',
        dataIndex: 'isPrimary',
        key: 'isPrimary',
        width: 120,
        render: (value: ReferralItem['isPrimary']) =>
          value ? <Tag color="gold">是</Tag> : '否',
      },
      {
        title: '分润比例',
        dataIndex: 'profitShareRatio',
        key: 'profitShareRatio',
        width: 110,
        render: (value: ReferralItem['profitShareRatio']) =>
          value !== undefined ? `${value}%` : '-',
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (value: ReferralItem['status']) =>
          value !== undefined ? value : '-',
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true,
        render: (value: ReferralItem['remark']) => value || '-',
      },
    ],
    [],
  );

  return (
    <UnifiedReferralSection
      title={detailSectionLabel('referrals')}
      hint={
        incomingReferrals.length
          ? `共 ${incomingReferrals.length} 条`
          : undefined
      }
      renderSection={({ title, hint, children }) => (
        <SectionPanel title={title} hint={hint}>
          {children}
        </SectionPanel>
      )}
      blocks={[
        {
          key: 'incoming',
          title: '谁介绍了当前商机',
          createButtonText: '新增',
          items: incomingReferrals,
          columns: referralColumns.filter((column) => column.key !== 'actions'),
          rowKey: (item) =>
            item.uuid || `${item.referrerUuid}-${item.createTime || ''}`,
          tableScroll: { x: 980 },
          emptyText: '',
          editor: {
            form,
            createTitle: '新增介绍来源',
            editTitle: '编辑介绍来源',
            submitting: saving,
            buildInitialValues: (record?: ReferralItem) =>
              buildOpportunityIncomingReferralFormValues(record),
            onOpen: (record) => {
              ensureCurrentOptions(record);
            },
            onSubmit: ({ values, mode, record }) =>
              submitEdit(values, mode, record),
            fields: [
              {
                type: 'radio',
                name: 'incomingReferrerType',
                label: '介绍人类型',
                rules: [{ required: true, message: '请选择介绍人类型' }],
                options: [
                  { label: '客户', value: 'customer' },
                  { label: '联系人', value: 'contact' },
                ],
                resetNames: ['incomingReferrerUuid'],
              },
              {
                type: 'select',
                name: 'incomingReferrerUuid',
                label: '谁介绍了当前商机',
                rules: [{ required: true, message: '请选择介绍人' }],
                options: ({ form }) =>
                  form.getFieldValue('incomingReferrerType') === 'contact'
                    ? contactOptions
                    : customerOptions,
                placeholder: ({ form }) =>
                  form.getFieldValue('incomingReferrerType') === 'contact'
                    ? '搜索并选择一个已有联系人'
                    : '搜索并选择一个已有客户',
                disabled: ({ form }) =>
                  !form.getFieldValue('incomingReferrerType'),
                onLoadOptions: (keyword, { form, record }) => {
                  const type = form.getFieldValue('incomingReferrerType') as
                    | ReferralItem['referrerType']
                    | undefined;
                  const ensureValues =
                    type === 'contact' && record?.referrerType === 'contact'
                      ? [
                          {
                            value: normalizeText(record.referrerUuid),
                            label: record.referrerName,
                          },
                        ]
                      : type === 'customer' &&
                          record?.referrerType === 'customer'
                        ? [
                            {
                              value: normalizeText(record.referrerUuid),
                              label: record.referrerName,
                            },
                          ]
                        : [];
                  if (type === 'contact') {
                    void loadContactReferralOptions(keyword, ensureValues);
                  } else if (type === 'customer') {
                    void loadCustomerReferralOptions(keyword, ensureValues);
                  }
                },
              },
              {
                type: 'date',
                name: 'incomingReferralTime',
                label: '介绍时间',
                showTime: true,
                placeholder: '请选择介绍时间',
              },
              {
                type: 'switch',
                name: 'incomingIsPrimary',
                label: '是否主介绍人',
                checkedChildren: '是',
                unCheckedChildren: '否',
              },
              {
                type: 'number',
                name: 'incomingProfitShareRatio',
                label: '分润比例',
                min: 0,
                max: 100,
                precision: 2,
                addonAfter: '%',
                placeholder: '请输入分润比例',
              },
              {
                type: 'textarea',
                name: 'incomingRemark',
                label: '备注',
                rows: 3,
                maxLength: 500,
                showCount: true,
                placeholder: '请输入备注',
              },
            ],
          },
          onDelete: removeReferral,
        },
      ]}
    />
  );
}

function ContactSection({
  page,
  relationContext,
  onRefresh,
}: {
  page: OpportunityDrawerRelatedPage<OpportunityDrawerContactItem>;
  relationContext: RelationCreateContext;
  onRefresh: () => void;
}) {
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [contactRelationOpen, setContactRelationOpen] = useState(false);

  return (
    <>
      <SectionPanel
        title={detailSectionLabel('contacts')}
        hint={pageHint(page, '联系人')}
        extra={
          <Space size={4}>
            <Button type="link" onClick={() => setContactCreateOpen(true)}>
              新建联系人
            </Button>
            <Button type="link" onClick={() => setContactRelationOpen(true)}>
              关联已有联系人
            </Button>
          </Space>
        }
      >
        {page.list.length ? (
          <CrmList
            dataSource={page.list}
            renderItem={(item) => (
              <CrmList.Item>
                <div className={styles.recordBlock}>
                  <div className={styles.recordTitleRow}>
                    <span className={styles.recordTitle}>
                      <ContactReferenceLink
                        contactUuid={item.uuid}
                        contactName={item.name || item.mobile || item.uuid}
                      />
                    </span>
                    {item.isPrimaryDecisionMaker ? (
                      <Tag color="green">关键决策人</Tag>
                    ) : null}
                  </div>
                  <Space wrap size={[8, 8]}>
                    {item.mobile ? <Tag>手机 {item.mobile}</Tag> : null}
                    {item.telephone ? <Tag>电话 {item.telephone}</Tag> : null}
                    {item.email ? <Tag>{item.email}</Tag> : null}
                    {item.post ? <Tag>{item.post}</Tag> : null}
                  </Space>
                  {item.remark ? (
                    <Paragraph className={styles.recordDescription}>
                      {item.remark}
                    </Paragraph>
                  ) : null}
                </div>
              </CrmList.Item>
            )}
          />
        ) : (
          <EmptyBlock description="" />
        )}
      </SectionPanel>
      <ContactEditModal
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
        currentMemberUuid={undefined}
        context={{
          customerUuid: relationContext.customerUuid,
          customerName: relationContext.customerName,
          lockCustomer: true,
        }}
        onSuccess={() => {
          setContactCreateOpen(false);
          onRefresh();
        }}
      />
      <ContactOpportunityCreateModal
        open={contactRelationOpen}
        context={relationContext}
        onCancel={() => setContactRelationOpen(false)}
        onSuccess={() => {
          setContactRelationOpen(false);
          onRefresh();
        }}
      />
    </>
  );
}

function QuoteSection({
  page,
  relationContext,
  ownerUserUuid,
  onRefresh,
}: {
  page: OpportunityDrawerRelatedPage<OpportunityDrawerQuoteItem>;
  relationContext: RelationCreateContext;
  ownerUserUuid?: string;
  onRefresh: () => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [quoteUuid, setQuoteUuid] = useState<string>();

  return (
    <>
      <SectionPanel
        title={detailSectionLabel('quotes')}
        hint={pageHint(page, '报价单')}
        extra={
          <Button
            type="link"
            onClick={() => {
              setModalMode('create');
              setQuoteUuid(undefined);
              setModalOpen(true);
            }}
          >
            新增报价单
          </Button>
        }
      >
        {
          <Table<OpportunityDrawerQuoteItem>
            rowKey="uuid"
            size="small"
            pagination={false}
            dataSource={page.list}
            columns={[
              {
                title: '报价单名称',
                key: 'name',
                render: (_, record) =>
                  record.uuid ? (
                    <a href={`/zy-crm/quote/${record.uuid}`}>
                      {record.name || record.code || record.uuid}
                    </a>
                  ) : (
                    record.name || record.code || '-'
                  ),
              },
              {
                title: '编号',
                dataIndex: 'code',
                key: 'code',
                render: (value?: string) => value || '-',
              },
              {
                title: '金额',
                dataIndex: 'totalPrice',
                key: 'totalPrice',
                render: (value?: string | number) =>
                  value ? `￥${formatMoney(value)}` : '-',
              },
              {
                title: '审核状态',
                dataIndex: 'auditStatus',
                key: 'auditStatus',
                render: (value?: string | number) =>
                  value !== undefined && value !== null ? String(value) : '-',
              },
              {
                title: '报价日期',
                key: 'quoteDate',
                render: (_, record) =>
                  formatCrmDate(record.quoteDate || record.updatedAt, 'date') ||
                  '-',
              },
              {
                title: '预计成交日期',
                dataIndex: 'expectedDealDate',
                key: 'expectedDealDate',
                render: (value?: string) => formatCrmDate(value, 'date') || '-',
              },
              {
                title: '备注',
                dataIndex: 'remark',
                key: 'remark',
                render: (value?: string) => value || '-',
              },
              {
                title: '操作',
                key: 'actions',
                render: (_, record) => (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      if (!record.uuid) {
                        message.warning('当前报价单缺少 UUID，无法编辑');
                        return;
                      }
                      setModalMode('edit');
                      setQuoteUuid(record.uuid);
                      setModalOpen(true);
                    }}
                  >
                    编辑
                  </Button>
                ),
              },
            ]}
            locale={{ emptyText: <QuietEmpty /> }}
          />
        }
      </SectionPanel>
      <QuoteEditorModal
        open={modalOpen}
        mode={modalMode}
        quoteUuid={quoteUuid}
        context={{
          customerUuid: relationContext.customerUuid,
          customerName: relationContext.customerName,
          lockCustomer: true,
          opportunityUuid: relationContext.opportunityUuid,
          opportunityName: relationContext.opportunityName,
          ownerUserUuid,
        }}
        onCancel={() => {
          setModalOpen(false);
          setQuoteUuid(undefined);
        }}
        onSuccess={() => {
          setModalOpen(false);
          setQuoteUuid(undefined);
          onRefresh();
        }}
      />
    </>
  );
}

function ContractSection({
  page,
  relationContext,
  ownerUserUuid,
  onRefresh,
}: {
  page: OpportunityDrawerRelatedPage<OpportunityDrawerContractItem>;
  relationContext: RelationCreateContext;
  ownerUserUuid?: string;
  onRefresh: () => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ContractEditModalMode>('create');
  const [recordUuid, setRecordUuid] = useState<string>();

  const openCreateModal = () => {
    setModalMode('create');
    setRecordUuid(undefined);
    setModalOpen(true);
  };

  const openEditModal = (uuid?: string) => {
    if (!uuid) {
      message.warning('当前合同缺少 UUID，无法编辑');
      return;
    }
    setModalMode('edit');
    setRecordUuid(uuid);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setRecordUuid(undefined);
  };

  return (
    <>
      <SectionPanel
        title={detailSectionLabel('contracts')}
        hint={pageHint(page, '合同')}
        extra={
          <Button type="link" onClick={openCreateModal}>
            新增合同
          </Button>
        }
      >
        {page.list.length ? (
          <CrmList
            dataSource={page.list}
            renderItem={(item) => (
              <CrmList.Item
                actions={[
                  ...(canEditContract(item)
                    ? [
                        <Button
                          key="edit"
                          type="link"
                          size="small"
                          onClick={() => openEditModal(item.uuid)}
                        >
                          编辑
                        </Button>,
                      ]
                    : []),
                  <ContractCenterSyncAction
                    key="sync-center"
                    contract={item}
                    onSynced={onRefresh}
                  />,
                ]}
              >
                <div className={styles.recordBlock}>
                  <div className={styles.recordTitleRow}>
                    <span className={styles.recordTitle}>
                      <a href={`/zy-crm/contract/${item.uuid}`}>
                        {item.name || item.code || item.uuid}
                      </a>
                    </span>
                    <Text type="secondary">
                      {formatCrmDate(item.orderDate, 'date')}
                    </Text>
                  </div>
                  <Space wrap size={[8, 8]}>
                    {item.code ? <Tag>{item.code}</Tag> : null}
                    {item.totalPrice ? (
                      <Tag color="blue">
                        金额 {formatMoney(item.totalPrice)} 元
                      </Tag>
                    ) : null}
                    {item.auditStatus !== undefined ? (
                      <Tag>审核 {item.auditStatus}</Tag>
                    ) : null}
                    {item.startTime ? (
                      <Tag>开始 {formatCrmDate(item.startTime, 'date')}</Tag>
                    ) : null}
                    {item.endTime ? (
                      <Tag>结束 {formatCrmDate(item.endTime, 'date')}</Tag>
                    ) : null}
                  </Space>
                  {item.remark ? (
                    <Paragraph className={styles.recordDescription}>
                      {item.remark}
                    </Paragraph>
                  ) : null}
                </div>
              </CrmList.Item>
            )}
          />
        ) : (
          <EmptyBlock description="" />
        )}
      </SectionPanel>

      <ContractEditModal
        open={modalOpen}
        mode={modalMode}
        recordUuid={recordUuid}
        context={{
          ownerUserUuid,
          customerUuid: relationContext.customerUuid,
          customerName: relationContext.customerName,
          opportunityUuid: relationContext.opportunityUuid,
          opportunityName: relationContext.opportunityName,
        }}
        onCancel={closeModal}
        onSuccess={async () => {
          closeModal();
          onRefresh();
        }}
      />
    </>
  );
}

function ProductSection({
  page,
}: {
  page: OpportunityDrawerRelatedPage<OpportunityDrawerProductItem>;
}) {
  return (
    <SectionPanel
      title={detailSectionLabel('products')}
      hint={pageHint(page, '产品')}
    >
      {
        <Table
          rowKey={(record) =>
            String(
              record.uuid ||
                record.offeringVersionUuid ||
                record.productCode ||
                record.productName ||
                '-',
            )
          }
          pagination={false}
          size="small"
          dataSource={page.list}
          columns={[
            {
              title: '产品名称',
              dataIndex: 'productName',
              render: (value, record) =>
                value ||
                record.productCode ||
                record.offeringVersionUuid ||
                '-',
            },
            {
              title: '产品编码',
              dataIndex: 'productCode',
              render: (value) => value || '-',
            },
            {
              title: '产品单位',
              dataIndex: 'productUnit',
              render: (value) => value || '-',
            },
            {
              title: '数量',
              dataIndex: 'quantity',
              render: (value) => value || '-',
            },
            {
              title: '标准价',
              dataIndex: 'standardPrice',
              render: (value) =>
                value ? `${formatMoney(value as string)} 元` : '-',
            },
            {
              title: '商机价',
              dataIndex: 'opportunityPrice',
              render: (value) =>
                value ? `${formatMoney(value as string)} 元` : '-',
            },
            {
              title: '小计',
              dataIndex: 'totalPrice',
              render: (value) =>
                value ? `${formatMoney(value as string)} 元` : '-',
            },
            {
              title: '更新时间',
              dataIndex: 'updatedAt',
              render: (value) => formatCrmDate(value),
            },
          ]}
          locale={{ emptyText: <QuietEmpty /> }}
        />
      }
    </SectionPanel>
  );
}
